import React, { useEffect, useState, Suspense, lazy } from 'react';
import { toast } from 'react-toastify';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import SocialEditor from './SocialEditor';
import { updateUser, registerUser, acknowledgePaymentNotice } from '../actions/registerUser';
import { loadPendingProfileEdit } from '../utils/pendingProfileEdit';
import firebase from '../config/fbConfig';
import Spinner from './Spinner';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { getButton } from './Views/socialstate.js';
import {
  validateAndDecodeImage,
  cropAndCompress,
  PROFILE_TARGET,
  COVER_TARGET,
} from '../utils/imageUpload';
import {
  DEFAULT_AVATAR,
  DEFAULT_COVER,
  handleAvatarError,
} from '../utils/imageFallback';
import CropLoadErrorBoundary from './CropLoadErrorBoundary';
import TwoFactorSettings from './TwoFactorSettings';
import AppTopBar from './AppTopBar';
import BackButton from './BackButton';
import EmailChangeControl from './EmailChangeControl';
import UsernameChangeControl from './UsernameChangeControl';
// Lazy-loaded: react-easy-crop only needs to be fetched once a user
// actually picks a file, not on every page load.
const CropModal = lazy(() => import('./CropModal'));

const EditProfile = ({
  loading,
  user,
  updateUser,
  isAuth,
  sessionExpired,
  acknowledgePaymentNotice,
}) => {
  // An edit that could not be saved because the session ended.
  //
  // Re-read on every run rather than captured once and consumed. This
  // effect fires again the moment redux's `user` arrives from
  // GET /api/auth/current, and a consume-once version had already
  // cleared itself by then -- so the second run overwrote the restored
  // name and bio with the server's copy. Observed doing exactly that on
  // staging: the entry list came back (it is seeded separately, from the
  // same stash) while the name and bio silently reverted.
  //
  // Spread over `user` so anything the stash does not carry still falls
  // back to the stored profile. The stash is removed as soon as a save
  // succeeds, which is what stops this applying forever.
  const [restoredNotice, setRestoredNotice] = useState(false);

  useEffect(() => {
    const pending = loadPendingProfileEdit();
    if (pending) {
      setuser({ ...user, ...pending, curpassword: '', newpassword: '' });
      setRestoredNotice(true);
    } else {
      setuser({ ...user, curpassword: '', newpassword: '' });
    }
    setloading(false);
  }, [user]);
  const history = useHistory();
  const location = useLocation();
  const [User, setuser] = useState();
  const [Loading, setloading] = useState(true);
  const [imgLoading, setimgLoading] = useState(false);
  const [coverLoading, setcoverLoading] = useState(false);
  const [compressingAvatar, setCompressingAvatar] = useState(false);
  const [compressingCover, setCompressingCover] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [cropField, setCropField] = useState(null); // 'avatar' | 'cover'
  const [activeChangePassword, setactiveChangePassword] = useState(false);
  const [showPass, setshowPass] = useState({
    curpassword: false,
    newpassword: false,
  });
  const [toggleGear, setToggleGear] = useState(false);

  // Lets the hamburger menu's "Settings" item land directly on the
  // settings panel (and "Edit Profile" land on the form) via
  // ?tab=settings / ?tab=profile, instead of always opening on the form
  // and requiring an extra in-page click.
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'settings') setToggleGear(true);
    else if (tab === 'profile') setToggleGear(false);
  }, [location.search]);

  const [state, setstate] = useState({
    avatarURL: '',
    coverUrl: '',
  });

  const resetCropState = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setPendingBlob(null);
    setCropField(null);
  };

  const handleFileChange = async (event, cover) => {
    const imageFile = event.target.files[0];
    event.target.value = '';
    if (!imageFile) return;

    if (cover) setCompressingCover(true);
    else setCompressingAvatar(true);

    let decoded;
    try {
      decoded = await validateAndDecodeImage(imageFile);
    } catch (err) {
      if (cover) setCompressingCover(false);
      else setCompressingAvatar(false);
      alert(err.message);
      return;
    }
    if (cover) setCompressingCover(false);
    else setCompressingAvatar(false);

    setPendingBlob(decoded);
    setCropField(cover ? 'cover' : 'avatar');
    setCropSrc(URL.createObjectURL(decoded));
  };

  const handleCropCancel = () => resetCropState();

  const handleCropLoadError = () => {
    resetCropState();
    setCompressingAvatar(false);
    setCompressingCover(false);
    alert(
      'Something went wrong loading the photo editor. Please try selecting your photo again.'
    );
  };

  const handleCropConfirm = async (croppedAreaPixels) => {
    const cover = cropField === 'cover';
    const blob = pendingBlob;
    resetCropState();

    const target = cover ? COVER_TARGET : PROFILE_TARGET;
    if (cover) setCompressingCover(true);
    else setCompressingAvatar(true);
    let prepared = blob;
    try {
      prepared = await cropAndCompress(blob, croppedAreaPixels, target);
    } catch (err) {
      console.warn('Crop/compression failed, uploading original file instead.', err);
    } finally {
      if (cover) setCompressingCover(false);
      else setCompressingAvatar(false);
    }

    if (cover) setcoverLoading(true);
    else setimgLoading(true);

    try {
      const storageRef = firebase.storage().ref();
      if (cover) {
        const ref = storageRef.child(`images/cover_${User.lengthId}`);
        await ref.put(prepared);
        const url = await ref.getDownloadURL();
        setstate((prev) => ({ ...prev, coverUrl: url }));
      } else {
        const ref = storageRef.child(`images/avatar_${User.lengthId}`);
        await ref.put(prepared);
        const url = await ref.getDownloadURL();
        setstate((prev) => ({ ...prev, avatarURL: url }));
      }
    } catch (err) {
      console.error(err);
      alert('Upload failed, please try again.');
    } finally {
      if (cover) setcoverLoading(false);
      else setimgLoading(false);
    }
  };
  // const handleUpdateUser = () => {
  //   if (state.avatarURL === '') {
  //     alert('Image is Required');
  //   }
  //   updateUser({ avatarUrl: state.avatarURL });
  // };

  const onChange = (e) => {
    setuser({ ...User, [e.target.name]: e.target.value });
  };
  const onSubmit = (socialEntries) => {
    console.log(User);
    if (
      compressingAvatar ||
      compressingCover ||
      imgLoading ||
      coverLoading ||
      cropSrc
    )
      return alert('Please wait for your photo to finish uploading.');
    if (
      (User.curpassword && !User.newpassword) ||
      (!User.curpassword && User.newpassword)
    )
      return alert('🚫 Invalid Password ');
    if (User.curpassword && User.newpassword && User.newpassword.length < 6)
      return alert('🚫 Password must be 6 or more characters');
    if (
      User.curpassword &&
      User.newpassword &&
      User.curpassword === User.newpassword
    )
      return alert('🚫 New Password cannot be same as old password');
    setloading(true);
    // The old `social` field on User (from the initial load) is dropped
    // here rather than spread through -- the new editor's save is
    // socialEntries-only (see route/api/users.js's POST /update_user),
    // and the server derives `social` itself from socialEntries now.
    const { social, ...userWithoutLegacySocial } = User;
    let updatedUser = { ...userWithoutLegacySocial, socialEntries };
    if (state.avatarURL) updatedUser.avatarUrl = state.avatarURL;
    if (state.coverUrl) updatedUser.coverUrl = state.coverUrl;
    updateUser(updatedUser, history);
    setTimeout(() => {
      setloading(false);
    }, 3000);
  };

  // Real auth state first: `loading` (from redux) is a third state,
  // distinct from isAuth -- while the initial GET /api/auth/current is
  // still in flight, isAuth reads false (its initial value) even for an
  // already-logged-in visitor. Checking isAuth before loading resolves
  // redirected a still-logged-in user to /register on every refresh.
  //
  // The local `Loading` below is unrelated -- it just tracks whether the
  // effect above has copied the (by now confirmed real) user prop into
  // the local editable `User` state yet, which the form below needs.
  // Read at render time rather than once on mount, so it is available
  // both when SocialEditor remounts after a failed save and when the
  // page has been reloaded entirely. Cleared as soon as a save succeeds.
  const pendingEdit = loadPendingProfileEdit();
  const pendingSocialEntries =
    pendingEdit && Array.isArray(pendingEdit.socialEntries) && pendingEdit.socialEntries.length
      ? pendingEdit.socialEntries
      : null;

  if (loading) return <Spinner />;
  if (!isAuth) return <Redirect to="/register" />;
  if (Loading) return <Spinner />;

  return (
    <div className="add-pro-photo edit-pro edit-pro2 social-link">
      <div className="container" style={{ padding: '0', overflow: 'hidden' }}>
        <div className="row">
          <div className="col-12">
            <AppTopBar
              // Page-specific, not part of the shared bar -- toggles
              // between the profile-edit form and the settings panel
              // (or, outside settings, leaves the page entirely for the
              // public profile).
              back={
                toggleGear ? (
                  <BackButton onClick={() => setToggleGear(false)} />
                ) : (
                  <BackButton to={`/${User.userName || User.lengthId}`} />
                )
              }
            />

            {/* The session ended while this form was open. It is
                deliberately a banner and not a redirect: everything the
                customer typed is still in the fields above and below,
                and sending them to /login would throw it away. Signing
                in opens in a new tab so this one -- and the edit in it --
                stays exactly where it is. */}
            {sessionExpired && (
              <div
                style={{
                  margin: '10px',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  background: '#fdf1dc',
                  color: '#8a5a12',
                  fontSize: '13px',
                  lineHeight: 1.5,
                }}
              >
                <strong>{sessionExpired}</strong>
                <div style={{ marginTop: '4px' }}>
                  Your changes are still here and have not been lost.{' '}
                  <a
                    href="/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#8a5a12', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    Sign in again in a new tab
                  </a>
                  , then come back and press Save.
                </div>
              </div>
            )}

            {/* Recovered after the session ended and the page was
                reloaded or navigated away from. */}
            {restoredNotice && !sessionExpired && (
              <div
                style={{
                  margin: '10px',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  background: '#eef3ff',
                  color: '#33468c',
                  fontSize: '13px',
                  lineHeight: 1.5,
                }}
              >
                We kept the changes you hadn't saved. Check them over and press Save.
              </div>
            )}

            {toggleGear ? (
              <>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  onSubmit(user.socialList || []);
                }}
                style={{
                  marginTop: '20px',
                }}
              >
                <div className="form-con">
                  {user.activatedAt ? (
                    // Activated: email changes go through their own
                    // request/verify flow, entirely decoupled from this
                    // form's submit -- see EmailChangeControl.js. Not
                    // wired into `User`/onChange at all, so it can never
                    // ride along with (or be overwritten by) the
                    // bio/social/password submit below.
                    <EmailChangeControl currentEmail={user.email} />
                  ) : (
                    // Not yet activated: unchanged -- still writes
                    // directly through the big submit below, no
                    // verification, exactly as before activation existed.
                    <div className="form-con">
                      <span>Login Email</span>
                      <input
                        name="email"
                        type="email"
                        value={User.email}
                        onChange={onChange}
                      />
                    </div>
                  )}
                  <UsernameChangeControl currentUsername={user.userName} />
                  <div
                    className="form-con login1"
                    style={{ margin: 'auto', width: 'fit-content' }}
                  >
                    <button
                      className="btn log-in"
                      onClick={(e) => {
                        e.preventDefault();
                        setactiveChangePassword(!activeChangePassword);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      Change Password
                    </button>
                  </div>
                  {activeChangePassword && (
                    <div style={{ margin: 'auto' }}>
                      <div
                        className="form-con my-name"
                        style={{ position: 'relative' }}
                      >
                        <span>Current Password</span>
                        <input
                          name="curpassword"
                          type={showPass.curpassword ? 'text' : 'password'}
                          value={User.curpassword}
                          onChange={onChange}
                          autocomplete="off"
                        />
                        <span
                          onClick={() =>
                            setshowPass({
                              ...showPass,
                              curpassword: !showPass.curpassword,
                            })
                          }
                          style={{
                            position: 'absolute',
                            height: '26px',
                            width: '26px',
                            right: '4px',
                            top: '40px',
                            left: 'unset',
                          }}
                        >
                          {!showPass.curpassword ? (
                            <AiOutlineEye />
                          ) : (
                            <AiOutlineEyeInvisible />
                          )}
                        </span>
                      </div>
                      <div
                        className="form-con my-name"
                        style={{ position: 'relative' }}
                      >
                        <span>New Password</span>
                        <input
                          name="newpassword"
                          type={showPass.newpassword ? 'text' : 'password'}
                          value={User.newpassword}
                          onChange={onChange}
                          autocomplete="off"
                        />
                        <span
                          onClick={() =>
                            setshowPass({
                              ...showPass,
                              newpassword: !showPass.newpassword,
                            })
                          }
                          style={{
                            position: 'absolute',
                            height: '26px',
                            width: '26px',
                            right: '4px',
                            top: '40px',
                            left: 'unset',
                          }}
                        >
                          {!showPass.newpassword ? (
                            <AiOutlineEye />
                          ) : (
                            <AiOutlineEyeInvisible />
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Username and (once activated) email each have their
                      own dedicated save action now -- UsernameChangeControl.js
                      and EmailChangeControl.js -- so this generic submit is
                      only still needed for two cases: the password fields
                      just above (which have no save button of their own),
                      and the not-yet-activated email path, which stays a
                      plain input with no dedicated control of its own (see
                      that branch's own comment above). Hidden otherwise, so
                      it doesn't read as a global "Save" for the whole
                      Settings page when it isn't one. */}
                  {(activeChangePassword || !user.activatedAt) &&
                    getButton(
                      'edit',
                      user.social,
                      () => {},
                      user.userName,
                      'submit'
                    )}
                </div>
              </form>
              <TwoFactorSettings />
              </>
            ) : (
              <>
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    className="cover"
                    style={{
                      backgroundColor:
                        (compressingCover || coverLoading) && 'white',
                      backgroundImage:
                        compressingCover || coverLoading
                          ? ``
                          : state.coverUrl !== ''
                          ? `url(${state.coverUrl})`
                          : User.coverUrl
                          ? `url(${User.coverUrl})`
                          : `url(${DEFAULT_COVER})`,
                    }}
                  >
                    {(compressingCover || coverLoading) && (
                      <>
                        <Spinner />
                        <p style={{ textAlign: 'center', fontSize: '12px' }}>
                          {compressingCover
                            ? 'Compressing photo…'
                            : 'Uploading photo…'}
                        </p>
                      </>
                    )}
                    <label htmlFor="coverimg" className="overlay">
                      &ensp;
                    </label>
                    <input
                      type="file"
                      id="coverimg"
                      name="coverimg"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, true)}
                      disabled={compressingCover || coverLoading || Boolean(cropSrc)}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <div
                    className="upload-btn-wrapper"
                    style={{
                      position: 'absolute',
                      bottom: '22px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        width: '125px',
                        height: '125px',
                      }}
                    >
                      {compressingAvatar || imgLoading ? (
                        <>
                          <Spinner />
                          <p style={{ textAlign: 'center', fontSize: '10px' }}>
                            {compressingAvatar ? 'Compressing…' : 'Uploading…'}
                          </p>
                        </>
                      ) : (
                        <img
                          src={
                            state.avatarURL !== ''
                              ? state.avatarURL
                              : User.avatarUrl || DEFAULT_AVATAR
                          }
                          id="tmpImg"
                          alt=""
                          onError={handleAvatarError}
                        />
                      )}
                      <input
                        type="file"
                        id="avatarimg"
                        name="avatarimg"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, null)}
                        disabled={compressingAvatar || imgLoading || Boolean(cropSrc)}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="avatarimg"
                        style={{
                          width: '100%',
                          height: '100%',
                          position: 'absolute',
                          left: '0',
                          zIndex: 99,
                        }}
                      />
                      <button className="btnn" type="button">
                        <i className="camera-icon"></i>
                      </button>
                      <small>Change Profile Photo</small>
                    </div>
                  </div>
                </div>
                <form>
                  <div className="form-con">
                    <span>Name</span>
                    <input
                      name="name"
                      type="text"
                      value={User.name}
                      onChange={onChange}
                    />
                  </div>
                  <div className="form-con">
                    <span>My Bio</span>
                    <textarea name="bio" value={User.bio} onChange={onChange} />
                  </div>
                </form>

                <h4>Social Networks</h4>
                <SocialEditor
                  onSubmit={onSubmit}
                  // A stashed edit wins over the server's copy.
                  //
                  // Not belt-and-braces: onSubmit sets Loading, which
                  // makes this component render a spinner and therefore
                  // *unmounts* SocialEditor. When it comes back three
                  // seconds later it re-seeds from initialEntries, so a
                  // failed save would otherwise drop any row the customer
                  // had just added -- name and bio survive because they
                  // live in this component's own state, but the entries
                  // do not. Observed doing exactly that on staging: the
                  // stash held 13 entries and the editor redrew with 12.
                  initialEntries={pendingSocialEntries || user.socialList}
                  paymentNoticeAcknowledged={user.paymentNoticeAcknowledged}
                  onAcknowledgePaymentNotice={acknowledgePaymentNotice}
                />
              </>
            )}
          </div>
        </div>
      </div>
      {cropSrc && (
        <CropLoadErrorBoundary onError={handleCropLoadError}>
          <Suspense fallback={<Spinner />}>
            <CropModal
              imageSrc={cropSrc}
              aspect={
                cropField === 'cover'
                  ? COVER_TARGET.cropAspect
                  : PROFILE_TARGET.cropAspect
              }
              cropShape={
                cropField === 'cover'
                  ? COVER_TARGET.cropShape
                  : PROFILE_TARGET.cropShape
              }
              onCancel={handleCropCancel}
              onConfirm={handleCropConfirm}
            />
          </Suspense>
        </CropLoadErrorBoundary>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.registerUser.user,
  isAuth: state.registerUser.isAuth,
  sessionExpired: state.registerUser.sessionExpired,
  loading: state.registerUser.loading,
});

export default connect(mapStateToProps, { updateUser, acknowledgePaymentNotice })(EditProfile);
